import { NgStyle } from '@angular/common';
import { ChangeDetectionStrategy, Component, input, output } from '@angular/core';
import { FormArray } from '@angular/forms';
import { QuestionItemTypeEnum } from '@examdojo/models/question';
import { ContentItemFormGroup, ContentItemViewerComponent } from '@examdojo/question-content-item';
import { SelectorBadgeComponent } from '@examdojo/ui/selector-badge';
import {
  CONTENT_ITEM_TYPE_TO_COLOR,
  CONTENT_ITEM_TYPE_TO_LONG_LABEL,
  CONTENT_ITEM_TYPE_TO_SHORT_LABEL,
} from '../question-item-editor.model';
import { StemFirstLineAlignDirective } from '../stem-label/stem-first-line-align.directive';
import { StemLabelComponent } from '../stem-label/stem-label.component';

@Component({
  selector: 'dojo-question-item-stem-content-viewer',
  imports: [
    ContentItemViewerComponent,
    StemFirstLineAlignDirective,
    SelectorBadgeComponent,
    NgStyle,
    StemLabelComponent,
  ],
  template: `
    <dojo-stem-label [label]="stemLabel()" class="mr-1 min-h-6 min-w-7 empty:hidden" />

    <div class="flex w-full gap-2 overflow-x-auto overflow-y-hidden" dojoStemFirstLineAlign>
      <dojo-content-item-viewer
        [contentItemsFormArray]="contentItemsFormArray()"
        [isAllowedToEditImage]="isAllowedToEditImage()"
        [isAllowedToEditTable]="isAllowedToEditTable()"
        (editItem)="editItem.emit($event)"
      />
    </div>

    <div class="flex items-center gap-2">
      @if (stemTotalMarks() && stemTotalMarks()! > 0) {
        <span class="text-primary text-md font-normal">[{{ stemTotalMarks() }}]</span>
      }
      @if (showSelectorBadge()) {
        <div
          class="wrap-badge"
          [ngStyle]="{
            '--after-color': 'var(--' + CONTENT_ITEM_TYPE_TO_COLOR[QuestionItemTypeEnum.Stem] + '-200)'
          }"
        >
          <dojo-selector-badge
            class="self-start"
            (clicked)="selectorBadgeClicked.emit()"
            [color]="CONTENT_ITEM_TYPE_TO_COLOR[QuestionItemTypeEnum.Stem]"
            [label]="CONTENT_ITEM_TYPE_TO_SHORT_LABEL[QuestionItemTypeEnum.Stem]"
            [active]="isActiveSelectorBadge() ?? false"
            [disabled]="isSelectorBadgeDisabled() ?? false"
          />
        </div>
      }
    </div>
  `,
  host: {
    class: 'flex items-start justify-between gap-1',
  },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class QuestionItemStemContentViewerComponent {
  readonly contentItemsFormArray = input.required<FormArray<ContentItemFormGroup>>();
  readonly isAllowedToEditImage = input<boolean>(false);
  readonly isAllowedToEditTable = input<boolean>(false);
  readonly editItem = output<{ item: ContentItemFormGroup; contentItemIndex: number }>();
  readonly isActiveSelectorBadge = input<boolean>();
  readonly isSelectorBadgeDisabled = input<boolean>();
  readonly selectorBadgeClicked = output<void>();
  readonly showSelectorBadge = input<boolean>();
  readonly stemTotalMarks = input<number>();

  readonly CONTENT_ITEM_TYPE_TO_COLOR = CONTENT_ITEM_TYPE_TO_COLOR;
  readonly CONTENT_ITEM_TYPE_TO_SHORT_LABEL = CONTENT_ITEM_TYPE_TO_SHORT_LABEL;
  readonly CONTENT_ITEM_TYPE_TO_LONG_LABEL = CONTENT_ITEM_TYPE_TO_LONG_LABEL;
  readonly QuestionItemTypeEnum = QuestionItemTypeEnum;

  readonly stemLabel = input<string>();
}
